import { useEffect, useState } from 'react';

import { useAuth } from '@/contexts/AuthContext';

interface UseIsSudoReturn {
  /** True only when the Firebase ID token carries the `sudo: true` custom claim. */
  isSudo: boolean;
  /** True while the claim is still being resolved on first render or refresh. */
  isLoading: boolean;
}

/**
 * useIsSudo — exposes the `sudo` Firebase custom claim to React.
 *
 * The claim is granted server-side via the `_set-sudo.ts` script and
 * lands in the user's ID token at the next refresh. We force a fresh
 * fetch (`getIdTokenResult(true)`) on first hook mount so a recently
 * granted claim is picked up without requiring a manual logout/login.
 *
 * The claim is cached in the React state for the lifetime of the
 * `firebaseUser` object — Firebase rotates the token transparently
 * when needed, but custom claim changes still require a force refresh.
 *
 * Used by:
 *   - `<SudoGuard>` route guard around `/admin/*`.
 *   - `<AdminSidebar>` to mark the panel visually as admin.
 *   - Future feature flags that depend on internal-team-only access.
 */
export function useIsSudo(): UseIsSudoReturn {
  const { firebaseUser, isLoading: authLoading } = useAuth();
  const [isSudo, setIsSudo] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!firebaseUser) {
      setIsSudo(false);
      setIsLoading(false);
      return;
    }
    (async () => {
      try {
        // forceRefresh=true pulls a brand new token so a freshly granted
        // sudo claim is visible without sign-out / sign-in roundtrip.
        const result = await firebaseUser.getIdTokenResult(true);
        if (cancelled) return;
        setIsSudo(result.claims['sudo'] === true);
      } catch {
        if (!cancelled) setIsSudo(false);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [firebaseUser, authLoading]);

  return { isSudo, isLoading };
}
